import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled, keyframes, alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import logo from '../../assets/Logo.png';

/* ===== Styles ===== */
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const floatingBubbles = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
  100% { transform: translateY(0) scale(1); }
`;

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  background: 'linear-gradient(-45deg, #60a5fa, #34d399, #3b82f6, #f472b6)',
  backgroundSize: '400% 400%',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    animation: `${floatingBubbles} 6s infinite ease-in-out`,
    [theme.breakpoints.down('sm')]: { width: '300px', height: '300px' }
  },
  '&::before': { top: '-100px', left: '-100px', animationDelay: '0s' },
  '&::after': { bottom: '-100px', right: '-100px', animationDelay: '-3s' }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  minWidth: 320,
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.8)
    : 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.3s ease-in-out',
  
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
  },
  
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },

  '& .MuiFormHelperText-root': {
    color: theme.palette.text.secondary,
  },

  '&:hover': { 
    transform: 'translateY(-5px)' 
  },
  
  [theme.breakpoints.up('lg')]: { 
    maxWidth: 500 
  },
  
  [theme.breakpoints.down('sm')]: { 
    maxWidth: '95%', 
    margin: theme.spacing(2) 
  }
}));

const ContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
  [theme.breakpoints.up('lg')]: { padding: theme.spacing(5) },
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(3) }
}));

const Logo = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: { width: 60, height: 60 }
}));

/* ===== Component ===== */
const ResetPassword = () => {
  const { token } = useParams();         // token from /reset-password/:token
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [bannerError, setBannerError] = useState('');
  const [success, setSuccess] = useState('');

  // If token is missing (bad URL), show error instantly
  useEffect(() => {
    if (!token) {
      setBannerError('Invalid or missing token.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');
    setBannerError('');
    setSuccess('');

    if (!password || !confirm) {
      setFieldError('Please fill in all fields.');
      return;
    }
    if (password !== confirm) {
      setFieldError('Passwords do not match.');
      return;
    }
    if (!token) {
      setBannerError('Invalid or missing token.');
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // IMPORTANT: backend expects token in the URL and password confirmation in the payload
      const { data } = await axios.post(
        `${apiUrl}/auth/reset-password/${token}`,
        {
          password,
          password_confirmation: confirm
        }
      );

      if (data?.success) {
        const msg = data?.message || 'Password reset successful! You can now log in.';
        setSuccess(msg);
        toast.success(msg, { theme: 'colored' });
        setTimeout(() => navigate('/'), 1500);
      } else {
        const msg = data?.message || 'Failed to reset password.';
        setBannerError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.password?.[0] ||
        'An error occurred. Please try again.';
      setBannerError(apiMsg);
      toast.error(apiMsg);
    }
  };

  return (
    <Container>
      <ToastContainer />
      <StyledCard>
        <ContentWrapper>
          <Stack spacing={3} alignItems="center">
            <Logo src={logo} alt="Logo" />
            <Typography variant="h4" component="h1">
              Reset Password
            </Typography>

            {bannerError && (
              <Typography color="error" textAlign="center">
                {bannerError}
              </Typography>
            )}

            <form onSubmit={handleSubmit} style={{ width: '100%' }} noValidate>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  error={!!fieldError}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  error={!!fieldError}
                  helperText={fieldError}
                />

                {success && (
                  <Typography color="success.main" textAlign="center">
                    {success}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  sx={{ height: { xs: 45, sm: 56 }, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                >
                  Reset Password
                </Button>
              </Stack>
            </form>
          </Stack>
        </ContentWrapper>
      </StyledCard>
    </Container>
  );
};

export default ResetPassword;
